import axios from "axios";
import { getToken } from "services/auth.service";
import {
  getCachedAuthorization,
  hasApplication,
  loadAuthorization,
} from "auth/authorization.service";

const API_URL = process.env.REACT_APP_API_URL;

export async function getUserApplications(userId) {
  const cachedAuthorization = getCachedAuthorization();
  const currentUserId = cachedAuthorization?.user?.userId ?? cachedAuthorization?.user?.UserId;
  if (cachedAuthorization && (!userId || Number(userId) === Number(currentUserId))) {
    return cachedAuthorization.applications.map(normalizeApplication);
  }

  if (getToken()) {
    try {
      const authorization = await loadAuthorization();
      const loadedUserId = authorization?.user?.userId ?? authorization?.user?.UserId;
      if (authorization && (!userId || Number(userId) === Number(loadedUserId))) {
        return authorization.applications.map(normalizeApplication);
      }
    } catch {
      // Fallback to the legacy endpoint below.
    }
  }

  const token = getToken();
  const response = await axios.get(`${API_URL}/api/Application/user/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
  return data.map(normalizeApplication);
}

export async function checkApplication(applicationName) {
  const authorization = getCachedAuthorization();
  if (authorization) {
    const allowed = hasApplication(applicationName, authorization);
    return {
      Success: true,
      HasApplication: allowed,
      success: true,
      hasApplication: allowed,
    };
  }

  const token = getToken();
  const response = await axios.get(`${API_URL}/api/Application/check`, {
    params: { permission: applicationName },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
}

export function normalizeApplicationName(value = "") {
  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export function normalizeApplication(application) {
  return {
    ...application,
    applicationId: application.applicationId ?? application.ApplicationId,
    applicationCode: application.applicationCode ?? application.ApplicationCode,
    applicationName: application.applicationName ?? application.ApplicationName,
    accessPermissionCode: application.accessPermissionCode ?? application.AccessPermissionCode,
    route: application.route ?? application.Route,
    // icon: application.icon ?? application.Icon,
    displayOrder: application.displayOrder ?? application.DisplayOrder ?? 0,
    description: application.description ?? application.Description,
    source: application.source ?? application.Source,
    sourceName: application.sourceName ?? application.SourceName,
  };
}

export function getApplicationKind(application) {
  const normalizedCode = normalizeApplicationName(application?.applicationCode ?? application?.ApplicationCode);
  const normalizedAccessCode = normalizeApplicationName(application?.accessPermissionCode ?? application?.AccessPermissionCode);
  const normalizedName = normalizeApplicationName(application?.applicationName ?? application?.ApplicationName);
  const normalizedDescription = normalizeApplicationName(application?.description ?? application?.Description);
  const combined = `${normalizedCode} ${normalizedAccessCode} ${normalizedName} ${normalizedDescription}`;

  if (
    normalizedAccessCode === "pms.system.access" ||
    normalizedCode.includes("systemapp") ||
    normalizedCode.includes("adminapp") ||
    combined.includes("quan tri he thong") ||
    combined.includes("quan ly he thong") ||
    combined.includes("system")
  ) {
    return "system";
  }

  if (
    normalizedAccessCode === "pms.subcontract.access" ||
    normalizedCode.includes("subcontractapp") ||
    combined.includes("giao khoan") ||
    combined.includes("subcontract")
  ) {
    return "subcontract";
  }

  if (
    normalizedAccessCode === "pms.equipment.access" ||
    normalizedCode.includes("equipmentapp") ||
    combined.includes("thiet bi") ||
    combined.includes("equipment")
  ) {
    return "equipment";
  }

  if (
    normalizedAccessCode === "pms.contract.access" ||
    normalizedCode.includes("contractapp") ||
    combined.includes("hop dong") ||
    combined.includes("contract")
  ) {
    return "contract";
  }

  return "development";
}

export function getApplicationRoute(application) {
  const explicitRoute = application?.route ?? application?.Route;
  if (explicitRoute) {
    return explicitRoute;
  }

  const appName = getApplicationKind(application);

  if (appName === "system") {
    return "/systems";
  }

  if (appName === "contract") {
    return "/projects";
  }

  if (appName === "subcontract") {
    return "/subcontracts";
  }

  if (appName === "equipment") {
    return "/equipment";
  }

  return `/applications/${application.applicationId ?? application.ApplicationId}`;
}

export function isAdminUser(user) {
  return user?.role === "Admin" || user?.roleName === "Admin" || user?.isAdmin === true;
}
